// pages/dashboard/Submissions.jsx
// List of all submissions with filters
// Steve can see all reports in one place

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const Submissions = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [total, setTotal] = useState(0);

  // Feedback responses used to live only inside Form Maker. The dashboard card
  // links here with ?tab=feedback, so honour that on first load.
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') === 'feedback' ? 'feedback' : 'assessments');
  const [feedback, setFeedback] = useState([]);
  const [feedbackTotal, setFeedbackTotal] = useState(0);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [expandedResponse, setExpandedResponse] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchReports();
  }, [filter]);

  useEffect(() => {
    if (tab === 'feedback' && feedback.length === 0) fetchFeedback();
    setSearchParams(tab === 'feedback' ? { tab: 'feedback' } : {}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const fetchFeedback = async () => {
    setFeedbackLoading(true);
    try {
      const response = await axios.get('/feedback/responses?limit=50');
      if (response.data.success) {
        setFeedback(response.data.data.responses || []);
        setFeedbackTotal(response.data.data.total || 0);
      }
    } catch (err) {
      console.error('Feedback fetch error:', err);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleDeleteResponse = async (r) => {
    const who = r.respondentName || r.respondentEmail || 'this response';
    if (!window.confirm(`Delete the feedback response from ${who}?\n\nThis cannot be undone.`)) return;
    setDeletingId(r._id);
    try {
      await axios.delete(`/feedback/responses/${r._id}`);
      setFeedback(prev => prev.filter(x => x._id !== r._id));
      setFeedbackTotal(prev => Math.max(0, prev - 1));
      if (expandedResponse === r._id) setExpandedResponse(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Could not delete this response.');
    } finally {
      setDeletingId(null);
    }
  };

  // Answers are stored per question with mixed value shapes (string, number,
  // array for checkbox and ranking), so normalise before display.
  const formatAnswer = (answer) => {
    if (answer === null || answer === undefined || answer === '') return 'No answer';
    if (Array.isArray(answer)) {
      return answer.length === 0
        ? 'No answer'
        : answer.map((v, i) => `${i + 1}. ${v}`).join('  |  ');
    }
    return String(answer);
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const url = filter === 'all' ? '/reports?limit=50' : `/reports?status=${filter}&limit=50`;
      const response = await axios.get(url);
      if (response.data.success) {
        setReports(response.data.data.reports);
        setTotal(response.data.data.total);
      }
    } catch (err) {
      console.error('Fetch reports error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const getStatusBadge = (status) => {
    const map = {
      pending_review: { bg: '#FEF9E7', color: '#A04000', label: 'Pending Review' },
      generating: { bg: '#EBF5FB', color: '#1A5276', label: 'Generating' },
      approved: { bg: '#E8F8EF', color: '#1E8449', label: 'Approved' },
      sent: { bg: '#E8F8EF', color: '#1E8449', label: 'Sent' },
    };
    const b = map[status] || { bg: '#F4F6F9', color: '#7F8C8D', label: status || 'Unknown' };
    return <span style={{ backgroundColor: b.bg, color: b.color, padding: '4px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: '500' }}>{b.label}</span>;
  };

  const navItems = [
    { label: 'Dashboard', path: '/admin/dashboard', icon: '📊' },
    { label: 'Submissions', path: '/admin/submissions', icon: '📋', active: true },
    { label: 'Transactions', path: '/admin/transactions', icon: '💳' },
    { label: 'Meetings', path: '/admin/meetings', icon: '📅' },
    { label: 'Enterprise', path: '/admin/enterprise', icon: '🏢' },
    { label: 'Form Maker', path: '/admin/forms', icon: '📝' },
    { label: 'Analytics', path: '/admin/analytics', icon: '📈' },
    { label: 'Settings', path: '/admin/settings', icon: '⚙️' },
  ];

  return (
    <div style={s.page}>
      <div style={s.sidebar}>
        <div style={s.sidebarLogo}>
          <span style={s.logoExec}>EXECUTIVE</span>
          <span style={s.logoSpring}>Springboard</span>
        </div>
        <nav style={s.nav}>
          {navItems.map(item => (
            <button key={item.path} style={{ ...s.navItem, ...(item.active ? s.navActive : {}) }} onClick={() => navigate(item.path)}>
              <span>{item.icon}</span><span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div style={s.sidebarFooter}>
          <div style={s.adminName}>{admin?.name}</div>
          <button style={s.logoutBtn} onClick={handleLogout}>Sign Out</button>
        </div>
      </div>

      <div style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Submissions</h1>
            <p style={s.subtitle}>
              {tab === 'assessments' ? `${total} total assessments` : `${feedbackTotal} feedback responses`}
            </p>
          </div>
        </div>

        {/* Assessment vs feedback */}
        <div style={s.tabs}>
          <button
            style={{ ...s.tabBtn, ...(tab === 'assessments' ? s.tabActive : {}) }}
            onClick={() => setTab('assessments')}
          >
            Assessments
          </button>
          <button
            style={{ ...s.tabBtn, ...(tab === 'feedback' ? s.tabActive : {}) }}
            onClick={() => setTab('feedback')}
          >
            Feedback Responses{feedbackTotal ? ` (${feedbackTotal})` : ''}
          </button>
        </div>

        {tab === 'feedback' ? (
          <div style={s.card}>
            {feedbackLoading ? (
              <p style={s.loadingText}>Loading feedback responses...</p>
            ) : feedback.length === 0 ? (
              <div style={s.empty}>
                <div style={s.emptyIcon}>💬</div>
                <p style={s.emptyTitle}>No feedback responses yet</p>
                <p style={s.emptyText}>Responses appear here once people complete a feedback form.</p>
              </div>
            ) : (
              <div>
                <div style={s.fbTableHead}>
                  <span>Respondent</span>
                  <span>Email</span>
                  <span>Form</span>
                  <span>Date</span>
                  <span>Actions</span>
                </div>
                {feedback.map(r => (
                  <div key={r._id}>
                    <div style={s.fbTableRow}>
                      <span style={s.name}>{r.respondentName || 'Anonymous'}</span>
                      <span style={s.cell}>{r.respondentEmail || 'Not provided'}</span>
                      <span style={s.cell}>{r.formId?.name || 'Deleted form'}</span>
                      <span style={s.cell}>{new Date(r.createdAt).toLocaleDateString()}</span>
                      <span style={s.fbActions}>
                        <button
                          style={s.btnReview}
                          onClick={() => setExpandedResponse(expandedResponse === r._id ? null : r._id)}
                        >
                          {expandedResponse === r._id ? 'Hide' : 'View Detail'}
                        </button>
                        <button
                          style={s.btnDeleteResponse}
                          disabled={deletingId === r._id}
                          title="Delete this response"
                          onClick={() => handleDeleteResponse(r)}
                        >
                          {deletingId === r._id ? '...' : 'Delete'}
                        </button>
                      </span>
                    </div>
                    {expandedResponse === r._id && (
                      <div style={s.fbDetail}>
                        {(r.answers || []).map((a, i) => (
                          <div key={i} style={s.fbAnswerBlock}>
                            <div style={s.fbQuestion}>{a.questionText}</div>
                            <div style={s.fbAnswer}>{formatAnswer(a.answer)}</div>
                          </div>
                        ))}
                        {(!r.answers || r.answers.length === 0) && (
                          <p style={s.emptyText}>This response has no recorded answers.</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
        <>
        {/* Filters */}
        <div style={s.filters}>
          {['all', 'pending_review', 'sent', 'generating'].map(f => (
            <button
              key={f}
              style={{ ...s.filterBtn, ...(filter === f ? s.filterActive : {}) }}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'All' : f === 'pending_review' ? 'Pending Review' : f === 'sent' ? 'Sent' : 'Generating'}
            </button>
          ))}
        </div>

        <div style={s.card}>
          {loading ? (
            <p style={s.loadingText}>Loading submissions...</p>
          ) : reports.length === 0 ? (
            <div style={s.empty}>
              <div style={s.emptyIcon}>📋</div>
              <p style={s.emptyTitle}>No submissions found</p>
              <p style={s.emptyText}>Submissions will appear here after users complete assessments.</p>
            </div>
          ) : (
            <div>
              <div style={s.tableHead}>
                <span>Name</span>
                <span>Email</span>
                <span>Company</span>
                <span>Type</span>
                <span>Status</span>
                <span>Date</span>
                <span>Actions</span>
              </div>
              {reports.map(report => (
                <div key={report._id} style={{
                  ...s.tableRow,
                  ...(report.paidReport?.status === 'pending_review' ? s.tableRowUrgent : {})
                }}>
                  <span style={s.name}>
                    {report.paidReport?.status === 'pending_review' && <span style={s.urgentDot}>●</span>}
                    {report.userId?.firstName} {report.userId?.lastName}
                  </span>
                  <span style={s.cell}>{report.userId?.email}</span>
                  <span style={s.cell}>{report.userId?.companyName || 'N/A'}</span>
                  <span style={s.cell}>
                    <span style={{ ...s.typeBadge, ...(report.userId?.userType === 'enterprise' ? s.typeEnt : s.typeInd) }}>
                      {report.userId?.userType || 'individual'}
                    </span>
                  </span>
                  <span style={s.cell}>{getStatusBadge(report.paidReport?.status)}</span>
                  <span style={s.cell}>{new Date(report.createdAt).toLocaleDateString()}</span>
                  <span style={s.cell}>
                    <button style={s.btnReview} onClick={() => navigate(`/admin/submissions/${report._id}`)}>
                      {report.paidReport?.status === 'pending_review' ? '⚡ Review' : 'View'}
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

const s = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: 'var(--font-primary)' },
  sidebar: { width: '240px', backgroundColor: 'var(--color-primary)', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  sidebarLogo: { display: 'flex', flexDirection: 'column', gap: '2px', padding: '24px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' },
  logoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '18px', fontWeight: '700', color: '#fff', lineHeight: 1 },
  nav: { flex: 1, padding: '16px 0' },
  navItem: { width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '14px', cursor: 'pointer', textAlign: 'left' },
  navActive: { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' },
  sidebarFooter: { padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' },
  adminName: { fontSize: '13px', fontWeight: '600', color: '#fff', marginBottom: '10px' },
  logoutBtn: { width: '100%', padding: '8px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'rgba(255,255,255,0.5)', fontSize: '12px', cursor: 'pointer' },
  main: { flex: 1, backgroundColor: 'var(--color-dashboard-bg)', padding: '32px', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '26px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '4px' },
  subtitle: { fontSize: '14px', color: 'var(--color-text-light)' },
  filters: { display: 'flex', gap: '8px', marginBottom: '20px' },
  tabs: { display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '1px solid var(--color-border)' },
  tabBtn: { padding: '10px 18px', background: 'none', border: 'none', borderBottom: '2px solid transparent', fontSize: '14px', fontWeight: '500', color: 'var(--color-text-light)', cursor: 'pointer' },
  tabActive: { color: 'var(--color-primary)', borderBottom: '2px solid var(--color-teal)', fontWeight: '600' },
  fbTableHead: { display: 'grid', gridTemplateColumns: '1.3fr 1.5fr 1.3fr 0.9fr 1.1fr', gap: '12px', padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--color-border)' },
  fbTableRow: { display: 'grid', gridTemplateColumns: '1.3fr 1.5fr 1.3fr 0.9fr 1.1fr', gap: '12px', padding: '14px 16px', alignItems: 'center', borderBottom: '1px solid var(--color-border)', fontSize: '14px' },
  fbActions: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  btnDeleteResponse: { backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', border: '1px solid var(--color-error)', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: '500', cursor: 'pointer' },
  fbDetail: { padding: '16px 20px 20px', backgroundColor: 'var(--color-off-white)', borderBottom: '1px solid var(--color-border)' },
  fbAnswerBlock: { marginBottom: '14px' },
  fbQuestion: { fontSize: '13px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '4px' },
  fbAnswer: { fontSize: '14px', color: 'var(--color-text)', lineHeight: '1.6', whiteSpace: 'pre-wrap' },
  filterBtn: { padding: '8px 16px', borderRadius: '100px', border: '1.5px solid var(--color-border)', backgroundColor: '#fff', color: 'var(--color-text-light)', fontSize: '13px', cursor: 'pointer', fontWeight: '500' },
  filterActive: { border: '1.5px solid var(--color-teal)', backgroundColor: 'var(--color-info-bg)', color: 'var(--color-teal)' },
  card: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)', padding: '24px' },
  loadingText: { padding: '40px', textAlign: 'center', color: 'var(--color-text-light)' },
  empty: { padding: '60px 20px', textAlign: 'center' },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyTitle: { fontSize: '18px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '8px' },
  emptyText: { fontSize: '14px', color: 'var(--color-text-light)' },
  tableHead: { display: 'grid', gridTemplateColumns: '1.2fr 1.5fr 1fr 0.8fr 1fr 0.7fr 0.6fr', gap: '12px', padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '1px solid var(--color-border)', marginBottom: '4px' },
  tableRow: { display: 'grid', gridTemplateColumns: '1.2fr 1.5fr 1fr 0.8fr 1fr 0.7fr 0.6fr', gap: '12px', padding: '12px', borderRadius: '8px', backgroundColor: 'var(--color-off-white)', alignItems: 'center', marginBottom: '4px' },
  tableRowUrgent: { backgroundColor: '#FFFBF0', border: '1px solid #F39C12' },
  urgentDot: { color: '#F39C12', marginRight: '6px', fontSize: '10px' },
  name: { fontSize: '14px', fontWeight: '600', color: 'var(--color-primary)' },
  cell: { fontSize: '13px', color: 'var(--color-text-light)' },
  typeBadge: { padding: '3px 8px', borderRadius: '100px', fontSize: '11px', fontWeight: '500' },
  typeInd: { backgroundColor: '#EBF5FB', color: '#1A5276' },
  typeEnt: { backgroundColor: '#E8F8EF', color: '#1E8449' },
  btnReview: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '12px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' },
};

export default Submissions;
